import { ProcessChangesHistory } from 'models'
import styles from './HistoryDialog.module.sass'
import { useFetchList } from 'store'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import useTermSearch from 'hooks/useTermSearch'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TextSearchInput from 'components/TextSearchInput/TextSearchInput'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import Datepicker, { DATE_FORMAT } from 'components/Datepicker/Datepicker'
import NoData from 'components/general/NoData/NoData'
import { useState } from 'react'
import moment from 'moment'

const ProcessHistoryItem = (props) => {
	const { item } = props
	const { i18n, screenSize } = useGlobal()

	const labels = {
		notDefined: i18n`Not defined`,
		injuredState: i18n`Injured state`,
		clinicalState: i18n`Clinical state`,
	}

	const clinicalStatusColor = item?.clinicalState?.value?.toLowerCase()

	let processInjuredStateColor = ''
	if (item?.injuredState === 'Aceite') {
		processInjuredStateColor = 'accepted'
	} else if (item?.injuredState === 'Recusado') {
		processInjuredStateColor = 'rejected'
	}

	return (
		<>
			{['small', 'medium'].includes(screenSize) ? (
				<HStack
					className={`${styles['history-row']} ${styles['--process-history']}`}
				>
					<VStack className={styles['author-and-state-container']}>
						<VStack className={styles['history-author-and-date']}>
							<small>
								<strong>
									{item?.modificationDate?.dateTimeFormat}
								</strong>
							</small>
							<small>
								{i18n`by`}{' '}
								<FuseHighlight
									matchedWords={
										item.$textHighlight?.matchedIndexes?.[
											'modificationUser'
										]
									}
									text={item?.modificationUser}
								/>
							</small>
						</VStack>
						<VStack justify='center'>
							<Tooltip text={labels.injuredState}>
								<HStack
									gap='xsmall'
									justify='flex-start'
									align='center'
								>
									<div
										className={`${styles['status']} ${
											styles[
												`--injured-state-${processInjuredStateColor}`
											]
										}`}
									/>
									<span>
										<small>
											{item?.injuredState ||
												labels.notDefined}
										</small>
									</span>
								</HStack>
							</Tooltip>
							<Tooltip text={labels.clinicalState}>
								<HStack
									gap='xsmall'
									justify='flex-start'
									align='center'
								>
									<div
										className={`${styles['status']} ${
											styles[
												`--clinical-status-${clinicalStatusColor}`
											]
										}`}
									/>
									<span>
										<small>
											{item?.clinicalState?.description ||
												labels.notDefined}
										</small>
									</span>
								</HStack>
							</Tooltip>
						</VStack>
					</VStack>
					<Tooltip text={item?.text || ''}>
						<small className={styles['history-text']}>
							<FuseHighlight
								matchedWords={
									item.$textHighlight?.matchedIndexes?.[
										'text'
									]
								}
								text={item?.text}
							/>
						</small>
					</Tooltip>
				</HStack>
			) : (
				<HStack
					className={`${styles['history-row']} ${styles['--process-history']}`}
				>
					<VStack className={styles['history-author-and-date']}>
						<small>
							<strong>
								{item?.modificationDate?.dateTimeFormat}
							</strong>
						</small>
						<small>
							{i18n`by`}{' '}
							<FuseHighlight
								matchedWords={
									item.$textHighlight?.matchedIndexes?.[
										'modificationUser'
									]
								}
								text={item?.modificationUser}
							/>
						</small>
					</VStack>
					<Tooltip text={item?.text || ''}>
						<small className={styles['history-text']}>
							<FuseHighlight
								matchedWords={
									item.$textHighlight?.matchedIndexes?.[
										'text'
									]
								}
								text={item?.text}
							/>
						</small>
					</Tooltip>
					<VStack
						justify='center'
						className={styles['history-status']}
					>
						<Tooltip text={labels.injuredState}>
							<HStack
								gap='xsmall'
								justify='flex-start'
								align='center'
							>
								<div
									className={`${styles['status']} ${
										styles[
											`--injured-state-${processInjuredStateColor}`
										]
									}`}
								/>

								<span>
									<small>
										{item?.injuredState ||
											labels.notDefined}
									</small>
								</span>
							</HStack>
						</Tooltip>
						<Tooltip text={labels.clinicalState}>
							<HStack
								gap='xsmall'
								justify='flex-start'
								align='center'
							>
								<div
									className={`${styles['status']} ${
										styles[
											`--clinical-status-${clinicalStatusColor}`
										]
									}`}
								/>

								<span>
									<small>
										{item?.clinicalState?.description ||
											labels.notDefined}
									</small>
								</span>
							</HStack>
						</Tooltip>
					</VStack>
				</HStack>
			)}
		</>
	)
}

const ProcessHistoryDialog = (props) => {
	const { i18n } = useGlobal()
	const { data } = props
	const { processId } = data

	const [date, setDate] = useState(undefined)

	const processChangesHistoryUri = `/clinical_processes/${processId}/history`

	const { contents } = useFetchList(
		[processChangesHistoryUri],
		[ProcessChangesHistory.fromJSON]
	)

	const [processChangesHistory] = contents

	const { searchMatches, onSearch } = useTermSearch(
		processChangesHistory?.sort(
			(a, b) =>
				b.modificationDate.timestamp - a.modificationDate.timestamp
		),
		['text', 'modificationUser']
	)

	const filteredSearchMatches = date
		? searchMatches.filter((elem) => {
				return moment(elem.modificationDate.momentDate).isSame(
					moment(date, DATE_FORMAT, true),
					'day'
				)
		  })
		: searchMatches

	return (
		<VStack gap='xsmall'>
			<HStack>
				<TextSearchInput
					className={styles['search-bar']}
					placeholder={i18n`Search by terms`}
					onKeyUp={(e) => {
						return onSearch(e)
					}}
					onChange={(e) => {
						return onSearch(e)
					}}
				/>
				<Datepicker
					className={styles['date-container']}
					placeholder={i18n.translate(DATE_FORMAT)}
					showFormat={false}
					initialDate={
						date ? moment(date, DATE_FORMAT, true) : undefined
					}
					type='date'
					onChange={(_, originalValue) => {
						if (originalValue.isValid?.()) {
							setDate(originalValue.format(DATE_FORMAT))
						} else {
							setDate(undefined)
						}
					}}
				/>
			</HStack>
			{!filteredSearchMatches?.length ? (
				<NoData
					className={styles['no-data-container']}
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={i18n`There are no **matches**.`}
				/>
			) : (
				<VStack
					className={`${styles['history-root']} ${styles['--process-history']}`}
				>
					{filteredSearchMatches?.map((item) => (
						<ProcessHistoryItem item={item} key={item.id} />
					))}
				</VStack>
			)}
		</VStack>
	)
}

export default ProcessHistoryDialog
